import CertificateBuilderModel from "~/models/tracked/certificateBuilderModel";
import CertificateMapping from "./certificateMapping";
import Guid from "~/helpers/guid";
import ObjectModelRelation from "~/models/objectModelRelations/objectModelRelation";
import Repo from "~/state/modules/repo";
import IRepo from "~/interfaces/repo";
import IDefaultSortOptions from "~/models/listViews/listView";
import TrackedArrayProxy from "../../form_builder/models/trackedArrayProxy";
import {Hash} from "~/interfaces/hashInterfaces";
import ObjectModelConditionalRuleSet from "~/models/objectModels/objectModelConditionalRuleSet";
import ObjectModelConditionalFields from "~/models/objectModels/objectModelConditionalFields";
import IConditionalRuleContainer from "~/models/conditionalRules/conditionalRuleContainer";
import ConditionalRuleSetFactory from "~/models/conditionalRules/conditionalRuleSetFactory";
import {RelationType} from "~/models/relationType";
import {ObjectHelper} from "~/helpers/objectHelper";
import {ConditionalFieldDefinition} from "~/models/conditionalRules/conditionalFieldDefinition";
import ObjectModelProperty from "~/models/objectModelProperties/objectModelProperty";
import ICertificateMappedRelationInfo from "./mappedRelationInfo";

export default class CertificateMappedModelRelation extends CertificateBuilderModel implements IConditionalRuleContainer {
  static repo: IRepo<CertificateMappedModelRelation> = new Repo<CertificateMappedModelRelation>('mapped_model_relations', CertificateMappedModelRelation);

  client_id: string;
  input_client_id: string;
  relation_path: ICertificateMappedRelationInfo[];
  default_sort_options: IDefaultSortOptions[];
  list_filter_conditional_rule_sets: Hash<ObjectModelConditionalRuleSet>;

  get rootName(): string {
    return 'mapped_model_relations';
  }

  constructor(mapping: CertificateMapping, json: any = {}) {
    super();
    this.defineProperty('client_id', json['client_id'] || Guid.create());
    this.defineProperty('input_client_id', json['input_client_id'] || this.input_client_id);
    const proxyRelationPathOptions = Object.assign([], {parent: this, key: 'relation_path'});
    this.defineProperty('relation_path', TrackedArrayProxy.create(proxyRelationPathOptions));
    const proxySortOptions = Object.assign([], {parent: this, key: 'default_sort_options'});
    this.defineProperty('default_sort_options', TrackedArrayProxy.create(proxySortOptions));

    for (const definition of ObjectModelConditionalFields.CERTIFICATE_MAPPED_MDOEL_RELATION_FIELDS) {
      const conditionalFieldName = `${definition.fieldName}_conditional_rule_sets`;
      this.except.push(conditionalFieldName);
      this[conditionalFieldName] = {};
      this.createEmbeddedObject[conditionalFieldName] = (parent, _json) => {
        return ConditionalRuleSetFactory.createForContainer(
          this,
          definition,
          {},
          true,
          false
        );
      };
      if (definition.defaultConditionalValue instanceof Function) {
        this.except.push(definition.fieldName);
        this.createEmbeddedObject[definition.fieldName] = definition.defaultConditionalValue;
      }
    }
  }

  get TRACKED_ARRAYS(): string[] {
    return ['default_sort_options', 'relation_path'];
  }

  get EMBEDDED_MODELS(): { [key: string]: { type: any; relationType: RelationType } } {
    const models = {
    };
    for (const definition of ObjectModelConditionalFields.CERTIFICATE_MAPPED_MDOEL_RELATION_FIELDS) {
      models[`${definition.fieldName}_conditional_rule_sets`] = {
        type: ObjectModelConditionalRuleSet,
        relationType: RelationType.dictionary,
      };
      if (definition.defaultConditionalValue instanceof Function) {
        models[definition.fieldName] = {
          type: Object,
          relationType: RelationType.singleObject,
        };
      }
    }
    return models;
  }

  get relation(): ObjectModelRelation {
    if (this.relation_path.length === 0) return null;
    const lastRelation = this.relation_path[this.relation_path.length - 1];
    return ObjectModelRelation.repo.find(lastRelation.relation_client_id);
  }

  get relationLabel(): string {
    if (this.relation_path.length === 0) return null;
    const lastRelation = this.relation_path[this.relation_path.length - 1];
    if (lastRelation.inverse) {
      return this.relation.labelTitleDisplay;
    } else {
      return this.relation.inverseLabelTitleDisplay;
    }
  }

  get filterRuleSet(): ObjectModelConditionalRuleSet | null {
    const values = Object.values(this.list_filter_conditional_rule_sets);
    return values.length ? values[0] : null;
  }

  // ******************** CONDITIONAL RULE CONTAINER ******************** //

  getConditionalRuleSets(fieldName: string): Hash<ObjectModelConditionalRuleSet> {
    return this[`${fieldName}_conditional_rule_sets`];
  }
  getSortedConditionalRuleSets(fieldName: string): ObjectModelConditionalRuleSet[] {
    const ruleSets = this.getConditionalRuleSets(fieldName);
    return ObjectHelper.toArray(ruleSets).sort((x, y) => x.sort_order < y.sort_order ? -1 : 1);
  }

  createNewRuleSet(fieldName: string, definition: ConditionalFieldDefinition): ObjectModelConditionalRuleSet {
    return new ObjectModelConditionalRuleSet(fieldName, definition);
  }

  getReferencedControl(id: string): ObjectModelProperty {
    return ObjectModelProperty.repo.find(id);
  }

  getReferencableControls(): ObjectModelProperty[] {
    // polymorphic relations are not currently supported
    const lastRelation = this.relation_path[this.relation_path.length - 1];
    const model = lastRelation.inverse ? this.relation.rootObjectModel : this.relation.related_object_models[0];
    return model.objectModelPropertiesArray;
  }

  // ******************** CONDITIONAL RULE CONTAINER END ******************** //

}